// Banner global : affiche le nombre de ventes en file et un bouton
// "Synchroniser maintenant". Auto-drain quand la connexion revient.

import { Button } from '@pharmaconnect/ui/web'
import { useEffect, useState } from 'react'

import { supabase } from '../lib/supabase'
import { logAudit } from '../services/audit'
import {
  countQueuedSales,
  listQueuedSales,
  removeQueuedSale,
  updateQueuedSale,
} from '../services/offlineQueue'

const drainQueueOnce = async (
  onProgress: (count: number) => void,
): Promise<{ drained: number; failed: number }> => {
  const queued = await listQueuedSales()
  let drained = 0
  let failed = 0
  for (const entry of queued) {
    try {
      const { data, error } = await supabase.rpc(
        'create_sale_with_items' as never,
        {
          p_pharmacy_id: entry.payload.pharmacy_id,
          p_payment_method: entry.payload.payment_method,
          p_items: entry.payload.items,
          p_patient_id: entry.payload.patient_id ?? null,
          p_discount: entry.payload.discount ?? 0,
          p_prescription_id: entry.payload.prescription_id ?? null,
          p_camerpay_uuid: entry.payload.camerpay_uuid ?? null,
          p_points_used: entry.payload.points_used ?? 0,
        } as never,
      )
      if (error) {
        await updateQueuedSale(entry.id, {
          attempts: entry.attempts + 1,
          last_error: error.message,
        })
        failed++
        continue
      }
      const sale = data as { id: string; total: number; payment_method: string }
      await logAudit({
        action: 'sale.create.offline_synced',
        entity_type: 'sale',
        entity_id: sale.id,
        changes: {
          local_id: entry.id,
          queued_at: entry.queued_at,
          total: sale.total,
          payment_method: sale.payment_method,
        },
      })
      await removeQueuedSale(entry.id)
      drained++
      onProgress(queued.length - drained)
    } catch (err) {
      await updateQueuedSale(entry.id, {
        attempts: entry.attempts + 1,
        last_error: err instanceof Error ? err.message : 'unknown',
      })
      failed++
    }
  }
  return { drained, failed }
}

export function PendingSalesBanner() {
  const [count, setCount] = useState(0)
  const [draining, setDraining] = useState(false)
  const [lastSync, setLastSync] = useState<string | null>(null)

  const refresh = async () => {
    try {
      setCount(await countQueuedSales())
    } catch {
      // IndexedDB indisponible (ex. mode privé) — silence
    }
  }

  const drain = async () => {
    if (draining) return
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return
    setDraining(true)
    try {
      const { drained, failed } = await drainQueueOnce(setCount)
      if (drained > 0) {
        setLastSync(new Date().toLocaleTimeString('fr-FR'))
      }
      if (failed > 0) {
        // On garde dans la file ; le drain suivant retentera
        console.warn(`[offline-queue] ${failed} sale(s) failed to sync, will retry`)
      }
      await refresh()
    } finally {
      setDraining(false)
    }
  }

  useEffect(() => {
    void refresh()
    const onOnline = () => void drain()
    window.addEventListener('online', onOnline)
    // Polling de sécurité — au cas où l'event "online" ne se déclenche pas
    const interval = setInterval(() => {
      void refresh()
      if (navigator.onLine) void drain()
    }, 30_000)
    return () => {
      window.removeEventListener('online', onOnline)
      clearInterval(interval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (count === 0) return null

  return (
    <div className="fixed bottom-4 right-4 z-40 flex max-w-xs flex-col gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 shadow-lg backdrop-blur">
      <div className="flex items-center gap-2">
        <span className="text-lg">⏳</span>
        <p className="text-sm font-semibold text-amber-400">
          {count} vente{count > 1 ? 's' : ''} en attente de synchronisation
        </p>
      </div>
      <p className="text-xs text-neutral-400">
        Les ventes encaissées hors-ligne sont sauvegardées et seront envoyées au serveur dès le
        retour de la connexion.
        {lastSync ? ` Dernière sync : ${lastSync}.` : ''}
      </p>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => void drain()}
        loading={draining}
        data-testid="drain-now"
      >
        Synchroniser maintenant
      </Button>
    </div>
  )
}
